import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Chip } from '@mui/material';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { api } from 'services/api';
import history from 'services/history';
import { AccessRule } from 'types/accessRule';
import { UserProfile } from 'types/userProfile';
import ProfileForm, { ProfileValidation } from '../ProfileForm';
import { countRules, markActive, toggleRule } from '../profileRules';

const EditProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [validation, setValidation] = useState<ProfileValidation>({});
  const [accessRules, setAccessRules] = useState<AccessRule[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [savedName, setSavedName] = useState('');

  const loadProfile = useCallback(() => {
    if (!id) return Promise.resolve();

    return Promise.all([api.get<UserProfile>(`/user-profiles/${id}`), api.get<AccessRule[]>('/access-rules-parents')])
      .then(([profileResponse, rulesResponse]) => {
        setUserProfile(profileResponse.data);
        setSavedName(profileResponse.data.name);
        setAccessRules(markActive(rulesResponse.data, profileResponse.data.access_rules));
      })
      .catch(err => {
        console.error(err);
        toast.error(err.response?.data?.error || 'Não foi possível carregar o perfil');
        history.push('/user-profiles');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadProfile().finally(() => setLoading(false));
  }, [loadProfile]);

  function goBack() {
    history.push('/user-profiles');
  }

  function handleNameChange(value: string) {
    setUserProfile(old => (old ? { ...old, name: value } : null));
  }

  function handleSubmit() {
    setSaving(true);

    api
      .put(`/user-profiles/${id}`, { userProfile, accessRules })
      .then(() => loadProfile())
      .then(() => toast.success('Perfil atualizado'))
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível salvar o perfil');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();

    yup
      .object()
      .shape({ name: yup.string().required('O nome é obrigatório') })
      .validate(userProfile)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch(err => setValidation({ [err.path]: err.message }));
  }

  // descarta o que foi digitado e recarrega o perfil
  function handleCancel() {
    setValidation({});
    setLoading(true);
    loadProfile().finally(() => setLoading(false));
  }

  function handleDelete() {
    setSaving(true);

    api
      .delete(`/user-profiles/${id}`)
      .then(() => {
        toast.success('Perfil excluído com sucesso!');
        history.push('/user-profiles');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível excluir o perfil');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  const users = userProfile?.users_count ?? 0;
  const { active, total } = countRules(accessRules);

  return (
    <>
      <Appbar title="Perfis de usuário" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Excluir"
          destructive
          title="Deseja excluir este perfil?"
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleDelete}
        />
      )}

      <PageShell>
        <RecordHeader title={savedName || 'Perfil'} backAction={goBack}>
          {userProfile && (
            <Chip size="small" variant="outlined" label={`${users} ${users === 1 ? 'usuário' : 'usuários'}`} />
          )}
          {userProfile && <Chip size="small" variant="outlined" label={`${active} de ${total} permissões`} />}
        </RecordHeader>

        <WorkArea>
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !userProfile ? (
              <NoData message="Não foi possível carregar o perfil" />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                <ProfileForm
                  name={userProfile.name}
                  onNameChange={handleNameChange}
                  validation={validation}
                  accessRules={accessRules}
                  onToggleRule={ruleId => setAccessRules(rules => toggleRule(rules, ruleId))}
                />

                {users > 0 && (
                  <Alert severity="warning" variant="outlined">
                    Este perfil tem {users} {users === 1 ? 'usuário' : 'usuários'} e não pode ser excluído. Troque o
                    perfil deles antes.
                  </Alert>
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {!loading && userProfile && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button
              color="error"
              size="small"
              disabled={saving || users > 0}
              onClick={() => setConfirmDialogOpen(true)}
            >
              Excluir perfil
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outlined" color="inherit" size="small" disabled={saving} onClick={handleCancel}>
                Cancelar
              </Button>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditProfile;
